package er.prateek.crossly.ui.screens.levelselect

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.coach_levels_kicker
import crossly.shared.generated.resources.coach_levels_next_body
import crossly.shared.generated.resources.coach_levels_next_title
import crossly.shared.generated.resources.coach_levels_resume_body
import crossly.shared.generated.resources.coach_levels_resume_title
import crossly.shared.generated.resources.coach_levels_tiers_body
import crossly.shared.generated.resources.coach_levels_tiers_title
import crossly.shared.generated.resources.levelselect_empty
import crossly.shared.generated.resources.levelselect_locked
import crossly.shared.generated.resources.levelselect_locked_tier
import crossly.shared.generated.resources.levelselect_tier_progress
import crossly.shared.generated.resources.levelselect_title
import crossly.shared.generated.resources.tier_easy
import crossly.shared.generated.resources.tier_expert
import crossly.shared.generated.resources.tier_hard
import crossly.shared.generated.resources.tier_medium
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.Puzzle
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.ui.coach.CoachOverlay
import er.prateek.crossly.ui.coach.reportCoachBounds
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.GameTopBar
import er.prateek.crossly.ui.components.IconLock
import er.prateek.crossly.ui.components.IconStar
import er.prateek.crossly.ui.components.LabeledProgress
import er.prateek.crossly.ui.components.LevelTile
import er.prateek.crossly.ui.components.LoadingState
import er.prateek.crossly.ui.components.StatChip
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith
import er.prateek.crossly.ui.theme.window
import org.jetbrains.compose.resources.stringResource

@Composable
private fun tierLabel(tier: DifficultyTier): String = when (tier) {
    DifficultyTier.EASY -> stringResource(Res.string.tier_easy)
    DifficultyTier.MEDIUM -> stringResource(Res.string.tier_medium)
    DifficultyTier.HARD -> stringResource(Res.string.tier_hard)
    DifficultyTier.EXPERT -> stringResource(Res.string.tier_expert)
}

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun LevelSelectScreen(
    startTier: DifficultyTier,
    onOpenPuzzle: (String) -> Unit,
    onBack: () -> Unit,
) {
    val window = MaterialTheme.window
    var puzzlesByTier by remember { mutableStateOf<Map<DifficultyTier, List<Puzzle>>?>(null) }
    var selectedTier by remember { mutableStateOf(startTier) }

    LaunchedEffect(Unit) {
        puzzlesByTier = DifficultyTier.entries.associateWith { tier ->
            AppContainer.contentRepository.puzzlesByTier(tier)
        }
    }

    fun selectTier(tier: DifficultyTier) {
        val puzzlesInTier = puzzlesByTier?.get(tier).orEmpty()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.TIER_SELECTED,
            mapOf(
                "tier" to tier.name,
                "unlocked" to (puzzlesByTier?.let { AppContainer.progressionManager.isTierUnlocked(tier, it) } ?: false),
                "solved_count" to puzzlesInTier.count { AppContainer.progressionManager.starsFor(it.id) > 0 },
                "total_count" to puzzlesInTier.size,
            ),
        )
        selectedTier = tier
    }

    var tiersBounds by remember { mutableStateOf(Rect.Zero) }
    var nextTileBounds by remember { mutableStateOf(Rect.Zero) }
    var resumeTileBounds by remember { mutableStateOf(Rect.Zero) }

    val hasResumeOnMap = AppContainer.savedSessionManager.session?.takeIf { !it.isDaily } != null
    val coachSteps = remember(hasResumeOnMap, puzzlesByTier) {
        if (puzzlesByTier == null) emptyList()
        else levelsCoachTour(
            hasResume = hasResumeOnMap,
            seenLevels = AppContainer.coachPrefsStore.hasSeenLevelsCoach(),
            seenResume = AppContainer.coachPrefsStore.hasSeenResumeCoach(),
        )
    }
    var coachIndex by remember { mutableStateOf(0) }
    var coachVisible by remember { mutableStateOf(false) }

    LaunchedEffect(coachSteps) {
        if (coachSteps.isEmpty()) {
            coachVisible = false
            return@LaunchedEffect
        }
        coachIndex = 0
        coachVisible = true
        AppContainer.analytics.logEvent(
            AnalyticsEvents.LEVELS_COACH_SHOWN,
            mapOf("steps" to coachSteps.size, "has_resume" to hasResumeOnMap),
        )
    }

    fun finishLevelsCoach(skipped: Boolean) {
        val steps = coachSteps
        AppContainer.coachPrefsStore.markLevelsCoachSeen()
        if (LevelsCoachStep.RESUME in steps) {
            AppContainer.coachPrefsStore.markResumeCoachSeen()
        }
        AppContainer.analytics.logEvent(
            AnalyticsEvents.LEVELS_COACH_FINISHED,
            mapOf("skipped" to skipped, "step" to (steps.getOrNull(coachIndex)?.name ?: "")),
        )
        coachVisible = false
    }

    LaunchedEffect(coachVisible, coachIndex, coachSteps, puzzlesByTier) {
        if (!coachVisible) return@LaunchedEffect
        if (coachSteps.getOrNull(coachIndex) != LevelsCoachStep.RESUME) return@LaunchedEffect
        val session = AppContainer.savedSessionManager.session?.takeIf { !it.isDaily } ?: return@LaunchedEffect
        val homeTier = puzzlesByTier?.entries
            ?.firstOrNull { (_, puzzles) -> puzzles.any { it.id == session.puzzleId } }
            ?.key
        if (homeTier != null && selectedTier != homeTier) selectedTier = homeTier
    }

    BackHandler(enabled = coachVisible) { finishLevelsCoach(true) }

    GameScreen(
        adPlacement = AdPlacement.LEVEL_SELECT,
        overlay = {
            if (coachVisible && coachSteps.isNotEmpty()) {
                val step = coachSteps[coachIndex.coerceIn(coachSteps.indices)]
                val copy = when (step) {
                    LevelsCoachStep.TIERS -> stringResource(Res.string.coach_levels_tiers_title) to
                        stringResource(Res.string.coach_levels_tiers_body)
                    LevelsCoachStep.NEXT -> stringResource(Res.string.coach_levels_next_title) to
                        stringResource(Res.string.coach_levels_next_body)
                    LevelsCoachStep.RESUME -> stringResource(Res.string.coach_levels_resume_title) to
                        stringResource(Res.string.coach_levels_resume_body)
                }
                CoachOverlay(
                    stepIndex = coachIndex,
                    stepCount = coachSteps.size,
                    title = copy.first,
                    body = copy.second,
                    hole = when (step) {
                        LevelsCoachStep.TIERS -> tiersBounds
                        LevelsCoachStep.NEXT -> nextTileBounds
                        LevelsCoachStep.RESUME -> resumeTileBounds
                    },
                    kicker = stringResource(Res.string.coach_levels_kicker),
                    holeCornerRadius = if (step == LevelsCoachStep.TIERS) 16.dp else 20.dp,
                    onNext = {
                        if (coachIndex >= coachSteps.lastIndex) finishLevelsCoach(false)
                        else coachIndex++
                    },
                    onSkip = { finishLevelsCoach(true) },
                )
            }
        },
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(Spacing.lg),
        ) {
            val tiers = puzzlesByTier
            val allStars = tiers?.values?.flatten()?.sumOf { AppContainer.progressionManager.starsFor(it.id) }

            GameTopBar(
                title = stringResource(Res.string.levelselect_title),
                onBack = { if (coachVisible) finishLevelsCoach(true) else onBack() },
                trailing = {
                    allStars?.let {
                        StatChip(
                            value = it.toString(),
                            icon = { tint -> IconStar(size = 14.dp, tint = tint) },
                            tint = MaterialTheme.game.gold,
                        )
                    }
                },
            )

            if (tiers == null) {
                LoadingState(modifier = Modifier.weight(1f))
                return@Column
            }

            val tierUnlocked = AppContainer.progressionManager.isTierUnlocked(selectedTier, tiers)
            val levels = tiers[selectedTier].orEmpty()

            if (window.isSideBySide) {
                // Wide: tiers become a persistent left rail, so switching tier
                // never scrolls the grid out from under the player.
                Row(
                    modifier = Modifier.fillMaxSize(),
                    horizontalArrangement = Arrangement.spacedBy(Spacing.xl),
                ) {
                    Column(
                        modifier = Modifier.width(220.dp).reportCoachBounds { tiersBounds = it },
                        verticalArrangement = Arrangement.spacedBy(Spacing.sm),
                    ) {
                        DifficultyTier.entries.forEach { tier ->
                            TierPill(
                                tier = tier,
                                label = tierLabel(tier),
                                selected = tier == selectedTier,
                                unlocked = AppContainer.progressionManager.isTierUnlocked(tier, tiers),
                                puzzles = tiers[tier].orEmpty(),
                                fillWidth = true,
                                onClick = { selectTier(tier) },
                            )
                        }
                    }
                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.spacedBy(Spacing.lg),
                    ) {
                        TierSummary(tier = selectedTier, label = tierLabel(selectedTier), puzzles = levels, unlocked = tierUnlocked)
                        LevelGrid(
                            levels = levels,
                            tier = selectedTier,
                            tierUnlocked = tierUnlocked,
                            onOpenPuzzle = onOpenPuzzle,
                            onNextTileBounds = { nextTileBounds = it },
                            onResumeTileBounds = { resumeTileBounds = it },
                            modifier = Modifier.weight(1f),
                        )
                    }
                }
            } else {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState())
                        .reportCoachBounds { tiersBounds = it },
                    horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
                ) {
                    DifficultyTier.entries.forEach { tier ->
                        TierPill(
                            tier = tier,
                            label = tierLabel(tier),
                            selected = tier == selectedTier,
                            unlocked = AppContainer.progressionManager.isTierUnlocked(tier, tiers),
                            puzzles = tiers[tier].orEmpty(),
                            fillWidth = false,
                            onClick = { selectTier(tier) },
                        )
                    }
                }
                TierSummary(tier = selectedTier, label = tierLabel(selectedTier), puzzles = levels, unlocked = tierUnlocked)
                LevelGrid(
                    levels = levels,
                    tier = selectedTier,
                    tierUnlocked = tierUnlocked,
                    onOpenPuzzle = onOpenPuzzle,
                    onNextTileBounds = { nextTileBounds = it },
                    onResumeTileBounds = { resumeTileBounds = it },
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}

@Composable
private fun LevelGrid(
    levels: List<Puzzle>,
    tier: DifficultyTier,
    tierUnlocked: Boolean,
    onOpenPuzzle: (String) -> Unit,
    onNextTileBounds: (Rect) -> Unit,
    onResumeTileBounds: (Rect) -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = MaterialTheme.game

    if (levels.isEmpty()) {
        Box(modifier = modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
            Text(
                text = stringResource(Res.string.levelselect_empty),
                style = MaterialTheme.typography.bodyMedium,
                color = colors.inkMuted,
                textAlign = TextAlign.Center,
            )
        }
        return
    }

    val inProgressId = AppContainer.savedSessionManager.session
        ?.takeIf { !it.isDaily }
        ?.puzzleId
    val inProgressPercent = AppContainer.savedSessionManager.session
        ?.takeIf { !it.isDaily }
        ?.percentComplete
    val inProgressOnThisTier = inProgressId != null && levels.any { it.id == inProgressId }

    // The first level the player hasn't starred yet — highlighted so there is
    // always one obvious "continue here" target, unless a saved game on this
    // tier already owns that highlight.
    val nextIndex = levels.indexOfFirst { AppContainer.progressionManager.starsFor(it.id) == 0 }

    LazyVerticalGrid(
        // Adaptive rather than a fixed count: 3 tiles on a phone, 8+ on a
        // desktop window, with no breakpoint bookkeeping.
        columns = GridCells.Adaptive(minSize = 104.dp),
        modifier = modifier.fillMaxWidth(),
        contentPadding = PaddingValues(vertical = Spacing.xs),
        horizontalArrangement = Arrangement.spacedBy(Spacing.md),
        verticalArrangement = Arrangement.spacedBy(Spacing.md),
    ) {
        items(levels.size) { index ->
            val puzzle = levels[index]
            val unlocked = AppContainer.progressionManager.isLevelUnlocked(tierUnlocked, levels, index)
            val resumePercent = if (puzzle.id == inProgressId) inProgressPercent else null
            val isNext = unlocked && resumePercent == null && index == nextIndex && !inProgressOnThisTier
            val spotlightNext = unlocked && (
                (nextIndex >= 0 && index == nextIndex) ||
                    (nextIndex < 0 && index == 0)
                )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(
                        when {
                            resumePercent != null -> Modifier.reportCoachBounds(onResumeTileBounds)
                            spotlightNext -> Modifier.reportCoachBounds(onNextTileBounds)
                            else -> Modifier
                        },
                    ),
                contentAlignment = Alignment.TopCenter,
            ) {
                LevelTile(
                    number = index + 1,
                    title = puzzle.title,
                    stars = AppContainer.progressionManager.starsFor(puzzle.id),
                    locked = !unlocked,
                    tierColor = colors.tierColor(tier),
                    isNext = isNext,
                    resumePercent = resumePercent,
                    modifier = Modifier.widthIn(max = 128.dp),
                    onClick = {
                        AppContainer.analytics.logEvent(
                            AnalyticsEvents.LEVEL_TILE_TAPPED,
                            mapOf(
                                "puzzle_id" to puzzle.id,
                                "tier" to tier.name,
                                "index" to index,
                                "stars" to AppContainer.progressionManager.starsFor(puzzle.id),
                                "is_resume" to (puzzle.id == inProgressId),
                                "is_next_recommended" to (index == nextIndex),
                            ),
                        )
                        onOpenPuzzle(puzzle.id)
                    },
                )
            }
        }
    }
}

@Composable
private fun TierSummary(
    tier: DifficultyTier,
    label: String,
    puzzles: List<Puzzle>,
    unlocked: Boolean,
) {
    val colors = MaterialTheme.game
    val solved = puzzles.count { AppContainer.progressionManager.starsFor(it.id) > 0 }

    GameCard(
        modifier = Modifier.fillMaxWidth(),
        contentPadding = PaddingValues(Spacing.lg),
        accentGlow = if (unlocked) colors.tierColor(tier).copy(alpha = 0.5f) else null,
    ) {
        if (!unlocked) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                IconLock(size = 18.dp, tint = colors.locked)
                Text(
                    text = stringResource(Res.string.levelselect_locked_tier),
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.inkMuted,
                )
            }
        } else {
            LabeledProgress(
                label = label,
                value = stringResource(Res.string.levelselect_tier_progress, solved, puzzles.size),
                progress = if (puzzles.isEmpty()) 0f else solved.toFloat() / puzzles.size,
                tint = colors.tierColor(tier),
            )
        }
    }
}

@Composable
private fun TierPill(
    tier: DifficultyTier,
    label: String,
    selected: Boolean,
    unlocked: Boolean,
    puzzles: List<Puzzle>,
    fillWidth: Boolean,
    onClick: () -> Unit,
) {
    val colors = MaterialTheme.game
    val tierColor = colors.tierColor(tier)
    val solved = puzzles.count { AppContainer.progressionManager.starsFor(it.id) > 0 }
    val shape = RoundedCornerShape(16.dp)

    // Selection lifts and saturates the pill instead of only recoloring text,
    // so the active tier is obvious at a glance on a dense screen.
    val emphasis by animateFloatAsState(if (selected) 1f else 0f, tween(240), label = "tierEmphasis")

    Row(
        modifier = Modifier
            // Fixed minimum width instead of wrap-content so Easy/Medium/Hard/Expert
            // render as same-sized pills regardless of label length or whether the
            // "x/x solved" line is present.
            .then(if (fillWidth) Modifier.fillMaxWidth() else Modifier.widthIn(min = 132.dp))
            .graphicsLayer { scaleX = 1f + 0.02f * emphasis; scaleY = 1f + 0.02f * emphasis }
            .clip(shape)
            .background(
                if (selected) tierColor.mixWith(colors.card, 0.15f) else colors.card.copy(alpha = 0.6f),
            )
            .border(
                width = if (selected) 2.dp else 1.5.dp,
                color = if (selected) tierColor else colors.cardBorder,
                shape = shape,
            )
            .clickable(onClick = onClick)
            .padding(horizontal = Spacing.lg, vertical = Spacing.md),
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .padding(end = Spacing.xxs)
                .clip(RoundedCornerShape(4.dp))
                .background(if (unlocked) tierColor else colors.locked)
                .width(4.dp)
                .padding(vertical = 10.dp),
        )
        Column(modifier = if (fillWidth) Modifier.weight(1f) else Modifier) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelLarge,
                color = if (selected) colors.ink else colors.inkMuted,
            )
            // Always a second line -- unlocked shows progress, locked shows why not --
            // so every pill in the row is the same height regardless of state.
            if (puzzles.isNotEmpty()) {
                Text(
                    text = if (unlocked) {
                        stringResource(Res.string.levelselect_tier_progress, solved, puzzles.size)
                    } else {
                        stringResource(Res.string.levelselect_locked)
                    },
                    style = MaterialTheme.typography.labelSmall,
                    // The selected pill is filled with the tier color, where the
                    // muted ink used on unselected pills washes out.
                    color = if (selected) colors.ink.copy(alpha = 0.8f) else colors.inkMuted,
                )
            }
        }
        if (!unlocked) {
            IconLock(size = 15.dp, tint = colors.locked)
        }
    }
}
